<template>
  <div class="ledger-page pb-5 pb-md-2">
    <b-card class="rounded-lg">
      <div class="d-flex justify-content-between align-items-start flex-wrap mb-1">
        <h4 class="mb-50">
          {{ $t('ledger_sup_title') }}
        </h4>
        <b-button
          v-if="hasPermission('create suppliers')"
          variant="primary"
          size="sm"
          class="rounded-pill"
          :to="{ name: 'ledger-suppliers-new' }"
        >
          {{ $t('ledger_sup_new') }}
        </b-button>
      </div>

      <b-row class="mb-1">
        <b-col
          cols="12"
          md="5"
        >
          <b-form-input
            v-model="keyword"
            :placeholder="$t('ledger_sup_search_ph')"
            @keyup.enter="reload"
          />
        </b-col>
        <b-col
          cols="6"
          md="2"
        >
          <b-button
            variant="outline-primary"
            class="rounded-pill"
            @click="reload"
          >
            {{ $t('ledger_search') }}
          </b-button>
        </b-col>
      </b-row>

      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>
      <div
        v-else-if="!rows.length"
        class="text-center text-muted py-4"
      >
        {{ $t('ledger_sup_empty') }}
      </div>
      <div v-else>
        <div class="d-none d-md-block">
          <b-table
            :items="rows"
            :fields="fields"
            :tbody-tr-class="rowClass"
            small
            hover
            responsive
            class="ledger-dense-table"
          >
            <template #cell(code)="data">
              <b-link :to="showRoute(data.item)">
                {{ data.item.code }}
              </b-link>
            </template>
            <template #cell(name)="data">
              {{ data.item.name || '—' }}
            </template>
            <template #cell(gl_account)="data">
              <span :class="{ 'text-muted small': glAccountListText(data.item).quiet }">
                {{ glAccountListText(data.item).text }}
              </span>
            </template>
            <template #cell(in_use)="data">
              {{ data.item.in_use ? $t('ledger_yes') : $t('ledger_no') }}
            </template>
          </b-table>
        </div>
        <div class="d-md-none">
          <article
            v-for="item in rows"
            :key="item.id"
            class="ledger-sup-card"
            :class="{ 'ledger-inactive': item.in_use === false }"
            @click="$router.push(showRoute(item))"
          >
            <div class="d-flex justify-content-between">
              <strong>{{ item.code }}</strong>
              <span>{{ item.in_use ? $t('ledger_yes') : $t('ledger_no') }}</span>
            </div>
            <div>{{ item.name || '—' }}</div>
            <div class="small text-muted">
              {{ item.phone || '—' }} · {{ item.city || '—' }}
            </div>
            <div class="small">
              {{ glAccountListText(item).text }}
            </div>
            <div class="small text-muted">
              {{ $t('ledger_sup_account_code') }}: {{ item.account_code || '—' }}
            </div>
          </article>
        </div>
        <b-pagination
          v-if="meta.total"
          v-model="page"
          class="mt-1"
          align="center"
          :total-rows="meta.total"
          :per-page="meta.per_page || 25"
          @change="onPage"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BButton, BTable, BLink, BSpinner, BPagination,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getSuppliers } from '@/api/accounting/suppliers'
import { listFrom, apiErrorMessage, glAccountListText } from '@/utils/ledger'

export default {
  name: 'SupplierList',
  components: {
    BCard, BRow, BCol, BFormInput, BButton, BTable, BLink, BSpinner, BPagination,
  },
  data() {
    return {
      loading: false,
      rows: [],
      meta: {},
      keyword: '',
      page: 1,
    }
  },
  computed: {
    fields() {
      return [
        { key: 'code', label: this.$t('ledger_sup_code') },
        { key: 'name', label: this.$t('ledger_sup_name') },
        { key: 'phone', label: this.$t('ledger_sup_phone') },
        { key: 'city', label: this.$t('ledger_sup_city') },
        { key: 'gl_account', label: this.$t('ledger_gl_account') },
        { key: 'account_code', label: this.$t('ledger_sup_account_code') },
        { key: 'in_use', label: this.$t('ledger_sup_in_use') },
      ]
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    glAccountListText,
    showRoute(item) {
      return { name: 'ledger-suppliers-view', params: { id: item.id } }
    },
    rowClass(item) {
      return item && item.in_use === false ? 'ledger-inactive' : ''
    },
    reload() {
      this.page = 1
      this.load()
    },
    onPage(page) {
      this.page = page
      this.load()
    },
    async load() {
      this.loading = true
      try {
        const params = { page: this.page, per_page: 25 }
        const keyword = String(this.keyword || '').trim()
        if (keyword) params.keyword = keyword
        const res = await getSuppliers(params)
        this.rows = listFrom(res)
        this.meta = res.meta || {}
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: apiErrorMessage(error, this.$t('ledger_error')),
            icon: 'AlertCircleIcon',
            variant: error?.response?.status === 422 ? 'warning' : 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-sup-card { border-bottom: 1px solid #eee; padding: 0.85rem 0.15rem; cursor: pointer; }
.ledger-inactive,
::v-deep .ledger-inactive { opacity: 0.55; }
</style>
